/**
 * Side panel listing every published version of a given instance model.
 *
 * Versions are immutable by contract — the panel is intentionally
 * read-only. Each row offers a "Clone as new version" shortcut that hands
 * off to the New entry route with the source payload pre-loaded.
 */

import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { listInstanceVersions } from "@/api/catalog";
import {
  ErrorBox,
  RefreshFailed,
  Spinner,
  isLoadingOrPaused,
} from "@/components/Spinner";
import { EmptyState } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import type { GatewayCatalogItem, GatewayInstanceKind } from "@/types/api";


/**
 * Compare two ``vX.Y.Z`` strings as semver tuples. Returns >0 if ``a`` is
 * newer than ``b``. Falls back to a localeCompare for malformed entries
 * so the panel never crashes on bad data — including segments missing
 * entirely (``v1.0`` → length 2), which would otherwise yield ``undefined``
 * that ``Number.isNaN`` *does not* trap (it only catches NaN itself).
 */
function compareSemver(a: string | null, b: string | null): number {
  if (!a || !b) return (a ?? "").localeCompare(b ?? "");
  // ``Number()`` is strict — ``Number("1abc")`` is ``NaN`` whereas
  // ``parseInt("1abc", 10)`` returns ``1`` and would silently corrupt the
  // ordering of any malformed entry slipping past the backend regex.
  const parse = (v: string): number[] =>
    v.replace(/^v/, "").split(".").map((n) => Number(n));
  const segsA = parse(a);
  const segsB = parse(b);
  const allSegs = [...segsA, ...segsB];
  if (
    segsA.length !== 3 ||
    segsB.length !== 3 ||
    allSegs.some((n) => typeof n !== "number" || Number.isNaN(n))
  ) {
    return a.localeCompare(b);
  }
  const [aM, am, ap] = segsA;
  const [bM, bm, bp] = segsB;
  return aM - bM || am - bm || ap - bp;
}

export interface GatewayVersionHistoryProps {
  kind: GatewayInstanceKind;
  model: string;
  /** Current version being edited — highlighted, not cliquable. */
  currentVersion: string;
}

export function GatewayVersionHistory({
  kind,
  model,
  currentVersion,
}: GatewayVersionHistoryProps) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  const versionsQuery = useQuery({
    queryKey: ["catalog", "gateway-versions", kind, model],
    queryFn: () => listInstanceVersions(token, kind, model),
    enabled: !!token,
  });

  if (isLoadingOrPaused(versionsQuery)) {
    return (
      <aside className="card max-w-xs">
        <h2 className="text-sm font-medium text-ink mb-3">
          Versions of {model}
        </h2>
        <Spinner />
      </aside>
    );
  }

  // Only an error with nothing to show replaces the list (see RefreshFailed).
  if (versionsQuery.isLoadingError) {
    return (
      <aside className="card max-w-xs">
        <h2 className="text-sm font-medium text-ink mb-3">
          Versions of {model}
        </h2>
        <ErrorBox error={versionsQuery.error} />
      </aside>
    );
  }

  const items: GatewayCatalogItem[] = [...(versionsQuery.data?.items ?? [])];
  items.sort((a, b) => compareSemver(b.version, a.version)); // desc

  return (
    <aside className="card max-w-xs">
      <h2 className="text-sm font-medium text-ink mb-1">
        Versions of {model}
      </h2>
      <p className="text-xs text-ink-muted mb-3">
        Versions are immutable — clone to publish a revision.
      </p>
      <RefreshFailed query={versionsQuery} className="mb-3" />
      {items.length === 0 && <EmptyState size="compact" title="No other version." />}
      <ul className="space-y-2">
        {items.map((entry) => {
          const version = entry.version ?? "";
          const isCurrent = version === currentVersion;
          return (
            <li
              key={version}
              className={`rounded border px-3 py-2 ${
                isCurrent
                  ? "border-brand-500 bg-brand-50"
                  : "border-line"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-ink">
                  {version}
                </span>
                {isCurrent && (
                  <span className="text-xs uppercase tracking-wide text-brand-600">
                    current
                  </span>
                )}
              </div>
              <p className="text-xs text-ink-muted font-mono mt-1">
                {formatDateTime(entry.updatedAt)}
              </p>
              {!isCurrent && (
                <div className="mt-2 flex gap-2">
                  <Link
                    to={`/catalog/gateways/instances/${encodeURIComponent(kind)}/${encodeURIComponent(model)}/${encodeURIComponent(version)}/edit`}
                    className="text-xs text-brand-600 hover:underline"
                  >
                    View
                  </Link>
                  <Link
                    to={`/catalog/gateways/new?cloneFrom=${encodeURIComponent(kind)}/${encodeURIComponent(model)}/${encodeURIComponent(version)}`}
                    className="text-xs text-brand-600 hover:underline"
                  >
                    Clone
                  </Link>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
